import React, { useState, useMemo, useEffect } from 'react';
import { FiList, FiSearch, FiCheck, FiFilter, FiRotateCcw } from 'react-icons/fi';
import Modal from '../../../components/Modal';

const ShareAssessmentModal = ({
  isOpen,
  onClose,
  assessments,
  assessmentVendors = [],
  departmentLabel,
  shareAssessmentIds,
  shareOrganizations,
  shareSelectedOrgs,
  shareSelectedUsers,
  shareUsers = [],
  shareFilteredUsers,
  shareUserSearch,
  shareLoading,
  shareDataLoading,
  onShare,
  onToggleAssessment,
  onToggleUser,
  onUserSearchChange,
}) => {
  const [deptFilter, setDeptFilter] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [isPublicShare, setIsPublicShare] = useState(false);

  // Reset checkbox and filters when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsPublicShare(false);
      setVendorFilter('');
      setDeptFilter('');
    }
  }, [isOpen]);

  const norm = (s) => (s || '').trim().toLowerCase();

  // All vendors (business organizations) to choose from.
  const availableVendors = useMemo(() => {
    const names = new Set();
    assessmentVendors.forEach(v => {
      const name = v.vendorName || v.name;
      if (name) names.add(name);
    });
    return Array.from(names).sort();
  }, [assessmentVendors]);

  // Assessments are NOT filtered by vendor. The assessment list is what you're sharing; the
  // vendor selection only controls who appears on the right (organization users vs. vendor
  // users). Any assessment can be shared with either an organization user or a vendor user.
  const visibleAssessments = assessments;

  // Select All / Deselect All operate on the currently visible (filtered) assessments only,
  // so a vendor filter can't accidentally select hidden vendors' assessments.
  const selectAllVisible = () => {
    visibleAssessments.forEach(a => {
      if (!shareAssessmentIds.includes(a.id)) onToggleAssessment(a.id);
    });
  };
  const deselectAllVisible = () => {
    visibleAssessments.forEach(a => {
      if (shareAssessmentIds.includes(a.id)) onToggleAssessment(a.id);
    });
  };

  // Base user list: when a vendor is selected, show that vendor's users (matched by their
  // organization) from the full user list; otherwise the org-scoped default list.
  const userBaseList = useMemo(() => {
    if (vendorFilter) {
      return shareUsers.filter(u => norm(u.organization) === norm(vendorFilter));
    }
    return shareFilteredUsers;
  }, [vendorFilter, shareUsers, shareFilteredUsers]);

  // Derive unique departments from the active base user list
  const availableDepts = useMemo(() => {
    const depts = new Set();
    userBaseList.forEach(u => {
      if (u.department) depts.add(u.department);
    });
    return Array.from(depts).sort();
  }, [userBaseList]);

  // Apply both name search and department filter on top of the base list
  const visibleUsers = useMemo(() => {
    return userBaseList.filter(user => {
      if (deptFilter && (user.department || '') !== deptFilter) return false;
      if (!shareUserSearch.trim()) return true;
      const search = shareUserSearch.toLowerCase();
      const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim().toLowerCase();
      return (
        fullName.includes(search) ||
        (user.username || '').toLowerCase().includes(search) ||
        (user.email || '').toLowerCase().includes(search)
      );
    });
  }, [userBaseList, shareUserSearch, deptFilter]);

  // Filter-aware user Select All / Deselect All (operates on the visible users only).
  const allVisibleUsersSelected =
    visibleUsers.length > 0 && visibleUsers.every(u => shareSelectedUsers.includes(u.id));
  const toggleSelectAllVisibleUsers = () => {
    if (allVisibleUsersSelected) {
      visibleUsers.forEach(u => { if (shareSelectedUsers.includes(u.id)) onToggleUser(u.id); });
    } else {
      visibleUsers.forEach(u => { if (!shareSelectedUsers.includes(u.id)) onToggleUser(u.id); });
    }
  };

  // Get selected assessment names
  const selectedAssessmentNames = useMemo(() => {
    return assessments
      .filter(a => shareAssessmentIds.includes(a.id))
      .map(a => a.assessmentDescription || `Assessment #${a.id}`);
  }, [assessments, shareAssessmentIds]);

  // Get selected user names — look across the full user pool so vendor users (which aren't in
  // the org-scoped list) still resolve.
  const selectedUserNames = useMemo(() => {
    const pool = shareUsers.length > 0 ? shareUsers : shareFilteredUsers;
    return pool
      .filter(u => shareSelectedUsers.includes(u.id))
      .map(u => {
        const firstName = u.firstName || '';
        const lastName = u.lastName || '';
        const fullName = `${firstName} ${lastName}`.trim();
        return fullName || u.username || `User ${u.id}`;
      });
  }, [shareUsers, shareFilteredUsers, shareSelectedUsers]);

  // Check if both assessments and users are selected
  const showCheckbox = shareAssessmentIds.length > 0 && shareSelectedUsers.length > 0;

  // Format names for display
  const formatNames = (names) => {
    if (names.length === 0) return '';
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]} and ${names[1]}`;
    return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Share Assessments"
      size="xl"
      footer={
        <div className="w-full flex justify-center">
          <button
            onClick={() => onShare(isPublicShare)}
            disabled={shareLoading}
            className="px-8 py-2.5 bg-[#F47F2E] text-white text-sm font-medium hover:bg-[#e06a1f] transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            {shareLoading && (
              <svg
                className="animate-spin h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            )}
            {shareLoading ? 'Sharing...' : 'Share'}
          </button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Subtitle */}
        <p className="text-sm text-[#919191]">Select the assessment, associated organization, intended consumers, and authorized users, then publish the assessment to securely share its responses and supporting evidence.</p>

        {/* Assessments to Share */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-[#919191]">
              Assessments to Share
              {departmentLabel && (
                <span className="ml-2 text-xs font-semibold text-[#0b79b8] bg-[#0b79b8]/10 px-2 py-0.5 rounded-full">
                  {departmentLabel} dept only
                </span>
              )}
            </h3>
            {visibleAssessments.length > 0 && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={selectAllVisible}
                  className="text-xs font-medium text-[#0077b5] hover:text-[#005a8c] transition-colors"
                >
                  Select All
                </button>
                <span className="text-[#919191]/40">|</span>
                <button
                  type="button"
                  onClick={deselectAllVisible}
                  className="text-xs font-medium text-[#0077b5] hover:text-[#005a8c] transition-colors"
                >
                  Deselect All
                </button>
              </div>
            )}
          </div>
          <div className="border border-[#919191]/30">
            <div className="max-h-40 overflow-y-auto">
              {visibleAssessments.map((a) => (
                <div
                  key={a.id}
                  onClick={() => onToggleAssessment(a.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 border-b last:border-b-0 cursor-pointer transition-colors ${
                    shareAssessmentIds.includes(a.id)
                      ? 'bg-[#0077b5]/10 border-[#0077b5]/20 text-[#53545a]'
                      : 'border-[#919191]/20 text-[#919191]'
                  }`}
                >
                  <div className={`w-4 h-4 border flex items-center justify-center flex-shrink-0 ${
                    shareAssessmentIds.includes(a.id)
                      ? 'bg-[#0077b5] border-[#0077b5]'
                      : 'border-[#919191]/50 bg-white'
                  }`}>
                    {shareAssessmentIds.includes(a.id) && (
                      <FiCheck className="text-white text-xs" />
                    )}
                  </div>
                  <FiList className={`text-base flex-shrink-0 ${shareAssessmentIds.includes(a.id) ? 'text-[#F47F2E]' : 'text-[#919191]/50'}`} />
                  <span className="text-sm">{a.assessmentDescription || `Assessment #${a.id}`}</span>
                </div>
              ))}
              {visibleAssessments.length === 0 && (
                <div className="px-4 py-6 text-center">
                  <p className="text-sm text-[#919191]">No assessments available</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Organization Display (auto-selected, no checkbox) */}
          <div>
            <h3 className="text-sm font-medium text-[#919191] mb-3">Organization</h3>
            <div className="space-y-4 mb-8">
                <div className="border border-[#919191]/30">
              {shareDataLoading ? (
                <div className="px-4 py-3 space-y-2">
                  <div className="h-8 bg-gray-200 animate-pulse" />
                </div>
              ) : shareOrganizations.filter(org => org.trustCenterEnabled).length > 0 ? (
                shareOrganizations
                  .filter(org => org.trustCenterEnabled)
                  .map(org => (
                    <div
                      key={org.id}
                      className="flex items-center gap-3 px-4 py-2.5 border-b last:border-b-0 bg-[#0077b5]/10 text-[#53545a]"
                    >
                      <FiList className="text-[#F47F2E] text-base flex-shrink-0" />
                      <span className="text-sm font-medium">{org.name}</span>
                    </div>
                  ))
              ) : (
                <div className="px-4 py-6 text-center">
                  <p className="text-sm text-[#919191]">No organization available</p>
                </div>
              )}
            </div>

            {/* Vendor filter — shows the selected vendor's users on the right (instead of the
                organization's users). Does not affect the assessment list. */}
            {availableVendors.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-medium text-[#919191]">Vendor</label>
                  {vendorFilter && (
                    <button
                      type="button"
                      onClick={() => { setVendorFilter(''); setDeptFilter(''); }}
                      className="inline-flex items-center gap-1 text-xs font-medium text-[#0077b5] hover:text-[#005a8c] transition-colors"
                      title="Reset to All Vendors"
                    >
                      <FiRotateCcw className="text-xs" /> Reset
                    </button>
                  )}
                </div>
                <div className="relative flex items-center">
                  <FiList className="absolute left-3 top-1/2 -translate-y-1/2 text-[#F47F2E] text-base flex-shrink-0 pointer-events-none" />
                  <select
                    value={vendorFilter}
                    onChange={(e) => { setVendorFilter(e.target.value); setDeptFilter(''); }}
                    className="w-full pl-9 pr-3 py-2.5 text-sm bg-white border border-[#919191]/30 focus:outline-none focus:ring-1 focus:ring-[#0b79b8] text-slate-600"
                    title="Show this vendor's users instead of the organization's users"
                  >
                    <option value="">All Vendors</option>
                    {availableVendors.map(v => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Sharing Agreement Checkbox - Only show when both assessments and users are selected */}
            {showCheckbox && (
              <div>
                <label className="flex items-start gap-3 cursor-pointer group">
                  <div className="flex items-center h-5">
                    <input
                      type="checkbox"
                      checked={isPublicShare}
                      onChange={(e) => setIsPublicShare(e.target.checked)}
                      className="w-4 h-4 border-[#919191]/50 text-[#0077b5] focus:ring-[#0077b5] focus:ring-offset-0 cursor-pointer"
                    />
                  </div>
                  <span className="text-sm text-[#53545a] leading-relaxed group-hover:text-[#0077b5] transition-colors">
                    I understand that I am sharing{' '}
                    <span className="font-semibold text-[#0077b5]">{formatNames(selectedAssessmentNames)}</span>
                    {' '}with{' '}
                    <span className="font-semibold text-[#0077b5]">{formatNames(selectedUserNames)}</span>
                    .
                  </span>
                </label>
              </div>
            )}
            </div>
          </div>

          {/* Select Users (Multi-select) */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-slate-600">
                Select User {visibleUsers.length > 0 && `(${visibleUsers.length})`}
                {shareSelectedUsers.length > 0 && (
                  <span className="ml-2 text-[#0b79b8] font-semibold">
                    • {shareSelectedUsers.length} selected
                  </span>
                )}
              </h3>
              {visibleUsers.length > 0 && (
                <button
                  type="button"
                  onClick={toggleSelectAllVisibleUsers}
                  className="text-xs text-[#0b79b8] hover:text-[#095a8a] font-medium"
                >
                  {allVisibleUsersSelected ? 'Deselect All' : 'Select All'}
                </button>
              )}
            </div>
            {shareDataLoading ? (
              <div className="border border-slate-200 px-4 py-3 space-y-2">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="h-10 bg-gray-200 animate-pulse" />
                ))}
              </div>
            ) : (
            <div className="border  border-slate-200">
                {/* Search + Department filter row */}
                <div className="flex border-b border-slate-200">
                  <div className="relative flex-1 border-r border-slate-200">
                    <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
                    <input
                      type="text"
                      value={shareUserSearch}
                      onChange={(e) => onUserSearchChange(e.target.value)}
                      placeholder="Search users..."
                      className="w-full pl-9 pr-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#0b79b8]"
                    />
                  </div>
                  <div className="relative flex items-center">
                    <FiFilter className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none" />
                    <select
                      value={deptFilter}
                      onChange={(e) => setDeptFilter(e.target.value)}
                      className="pl-8 pr-3 py-2.5 text-sm bg-white border-0 focus:outline-none focus:ring-1 focus:ring-[#0b79b8] text-slate-600 min-w-[130px]"
                    >
                      <option value="">All Departments</option>
                      {availableDepts.map(d => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="max-h-56 overflow-y-auto">
                  {visibleUsers.map((user) => (
                    <div
                      key={user.id}
                      onClick={() => onToggleUser(user.id)}
                      className={`flex items-center justify-between px-4 py-3 cursor-pointer transition-colors border-b last:border-b-0 ${
                        shareSelectedUsers.includes(user.id)
                          ? 'bg-[#0b79b8]/10 text-[#53545a]'
                          : 'border-slate-100 hover:bg-slate-50 text-slate-700'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-4 h-4 border flex items-center justify-center flex-shrink-0 ${
                          shareSelectedUsers.includes(user.id)
                            ? 'bg-[#0b79b8] border-[#0b79b8]'
                            : 'border-slate-300 bg-white'
                        }`}>
                          {shareSelectedUsers.includes(user.id) && (
                            <FiCheck className="text-white text-xs" />
                          )}
                        </div>
                        <div className="flex flex-col">
                          <span className="text-sm font-medium">
                            {user.firstName || user.lastName 
                              ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                              : user.username || `User ${user.id}`}
                                {
                            user.role && (
                              <span className="text-xs mt-0.5 text-slate-400 italic">&nbsp;{`(${user.role})`}</span>
                            )
                          }
                          </span>
                          {user.email && (
                            <span className="text-xs mt-0.5 text-slate-500">{user.email}</span>
                          )}
                          {user.department && (
                            <span className="text-xs mt-0.5 text-[#0b79b8]/70 font-medium">{user.department}</span>
                          )}
                        
                        </div>
                      </div>
                    </div>
                  ))}
                  {visibleUsers.length === 0 && (
                    <div className="px-4 py-8 text-center">
                      <p className="text-sm text-slate-500">
                        {deptFilter
                          ? `No users in the "${deptFilter}" department`
                          : shareUserSearch.trim()
                          ? 'No users match your search'
                          : vendorFilter
                          ? `No users found for vendor "${vendorFilter}"`
                          : 'No users found for this organization'}
                      </p>
                    </div>
                  )}
                </div>
            </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ShareAssessmentModal;
